import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { array, boolean, object, string } from 'yup';
import { Decision } from '../../../../common/constants/decision';
import { ReasonsEnum as Reasons } from '../../../../common/constants/reasons';

export interface DeclineFormData {
  updateReason?: string;
  decision: Decision;
  reasons: Reasons[];
  declineDescription: string;
  declineConditions?: string[];
  mibCodes?: string[];
  triggerClr: boolean;
  haltClrReasons?: string[];
}

const initialFormData: Partial<DeclineFormData> = {
  decision: Decision.DECLINED,
  declineDescription: '',
  reasons: [],
  declineConditions: [],
  mibCodes: [],
  updateReason: '',
  triggerClr: true,
  haltClrReasons: [],
};

const reasons = Object.values(Reasons);
const decisions = Object.values(Decision);

const getSchema = ({ isUpdate }: DeclineFormOptions) =>
  object().shape({
    updateReason: isUpdate ? string().required('Required field') : string(),
    decision: string<Decision>().oneOf(decisions).required('Required field'),
    reasons: array(string<Reasons>().oneOf(reasons).required('Invalid reason'))
      .min(1, 'Required field')
      .required('Required field'),
    declineDescription: string().required('Required field'),
    declineConditions: array(string().required('Invalid condition')),
    mibCodes: array(string().required('Invalid code')),
    triggerClr: boolean().required(),
    haltClrReasons: array(string().required('Invalid reason')).when(
      'triggerClr',
      {
        is: false,
        then: schema => schema.min(1, 'At least one reason required'),
        otherwise: schema => schema,
      },
    ),
  });

export interface DeclineFormOptions {
  isUpdate: boolean;
}

const useDeclineForm = (options: DeclineFormOptions) => {
  const { control, watch, handleSubmit, reset, setValue } =
    useForm<DeclineFormData>({
      defaultValues: initialFormData,
      resolver: yupResolver(getSchema(options)),
      mode: 'onChange',
      reValidateMode: 'onChange',
    });

  const clear = useCallback(() => {
    reset(initialFormData);
  }, [reset]);

  return { control, watch, handleSubmit, reset, clear, setValue };
};

export default useDeclineForm;
